<template>
  <div class="processing-toolbox">
    <div class="tool-grid">
      <aside class="tool-list">
        <div v-for="category in categories" :key="category" class="tool-category">
          <div>{{ category }}</div>
          <button v-for="tool in toolsByCategory(category)" :key="tool.id" :class="{ active: selectedId === tool.id }" @click="selectedId = tool.id">{{ tool.name }}</button>
        </div>
      </aside>
      <section class="tool-config" v-if="tool">
        <h3>{{ tool.name }}</h3>
        <p>{{ tool.description }}</p>
        <label class="form-field">Input layer
          <select v-model="inputLayerId"><option v-for="layer in layers" :key="layer.id" :value="layer.id">{{ layer.name }} · {{ layer.metadata.featureCount.toLocaleString() }}</option></select>
        </label>
        <label v-if="tool.requiresOverlay" class="form-field">Overlay layer
          <select v-model="overlayLayerId"><option value="">Choose overlay layer</option><option v-for="layer in overlayCandidates" :key="layer.id" :value="layer.id">{{ layer.name }} · {{ layer.metadata.featureCount.toLocaleString() }}</option></select>
        </label>
        <template v-for="parameter in tool.parameters">
          <label v-if="parameter.type === 'select'" :key="parameter.id" class="form-field">{{ parameter.label }}
            <select v-model="parameters[parameter.id]"><option v-for="option in parameter.options" :key="option" :value="option">{{ option }}</option></select>
          </label>
          <label v-else-if="parameter.type === 'field'" :key="parameter.id" class="form-field">{{ parameter.label }}
            <select v-model="parameters[parameter.id]"><option value="">All features together</option><option v-for="field in inputFields" :key="field.name" :value="field.name">{{ field.name }} · {{ field.type }}</option></select>
          </label>
          <label v-else :key="parameter.id" class="form-field">{{ parameter.label }}
            <input v-model="parameters[parameter.id]" :min="parameter.min" :type="parameter.type === 'number' ? 'number' : 'text'">
          </label>
        </template>
        <p v-if="tool.requiresOverlay" class="tool-note">Overlay operations currently require polygon geometry in both layers.</p>
        <p class="tool-note">A new output layer will be created; the input data remains unchanged.</p>
      </section>
    </div>
    <footer><button class="secondary" @click="$emit('close')">Cancel</button><button class="primary" :disabled="!canRun" @click="run">Run {{ tool && tool.name }}</button></footer>
  </div>
</template>

<script>
export default {
  name: 'ProcessingToolbox',
  props: { tools: { type: Array, required: true }, layers: { type: Array, required: true }, activeLayer: { type: Object, default: null }, initialToolId: { type: String, default: 'buffer' } },
  data() {
    return { selectedId: this.initialToolId, inputLayerId: this.activeLayer && this.activeLayer.id || (this.layers[0] && this.layers[0].id), overlayLayerId: '', parameters: {} }
  },
  computed: {
    tool() { return this.tools.find(tool => tool.id === this.selectedId) || this.tools[0] },
    categories() { return [...new Set(this.tools.map(tool => tool.category))] },
    overlayCandidates() { return this.layers.filter(layer => layer.id !== this.inputLayerId) },
    inputLayer() { return this.layers.find(layer => layer.id === this.inputLayerId) || null },
    inputFields() { return this.inputLayer && this.inputLayer.metadata.schema || [] },
    canRun() { return this.tool && this.inputLayerId && (!this.tool.requiresOverlay || this.overlayLayerId) }
  },
  watch: {
    selectedId() { this.resetParameters() },
    inputLayerId() { if (this.overlayLayerId === this.inputLayerId) this.overlayLayerId = '' }
  },
  mounted() { this.resetParameters() },
  methods: {
    toolsByCategory(category) { return this.tools.filter(tool => tool.category === category) },
    resetParameters() { this.parameters = (this.tool.parameters || []).reduce((values, parameter) => ({ ...values, [parameter.id]: parameter.default }), {}) },
    run() {
      const parameters = Object.entries(this.parameters).reduce((values, [key, value]) => ({ ...values, [key]: typeof value === 'string' && value !== '' && !Number.isNaN(Number(value)) ? Number(value) : value }), {})
      this.$emit('run', { toolId: this.tool.id, inputLayerId: this.inputLayerId, overlayLayerId: this.overlayLayerId, parameters })
    }
  }
}
</script>

<style scoped>
.tool-grid { display: grid; grid-template-columns: 155px minmax(0, 1fr); min-height: 270px; }.tool-list { background: #0d1721; border-right: 1px solid var(--border); max-height: 400px; overflow: auto; padding: 6px; }.tool-category { margin-bottom: 10px; }.tool-category > div { color: var(--subtle); font-size: 9px; font-weight: 700; letter-spacing: .08em; padding: 5px 6px; text-transform: uppercase; }.tool-category button { background: transparent; border: 0; color: var(--muted); cursor: pointer; display: block; font: inherit; font-size: 11px; padding: 6px; text-align: left; width: 100%; }.tool-category button:hover, .tool-category button.active { background: var(--active); color: var(--text); }.tool-config { max-height: 400px; overflow: auto; padding: 14px; }.tool-config h3 { color: var(--text); font-size: 13px; margin: 0 0 6px; }.tool-config > p { color: var(--muted); font-size: 11px; line-height: 1.5; margin: 0 0 10px; }.tool-note { border-left: 2px solid #47779b; color: var(--subtle) !important; margin-top: 12px !important; padding-left: 7px; }.processing-toolbox > footer { border-top: 1px solid var(--border); display: flex; gap: 7px; justify-content: flex-end; padding: 10px 14px; }
</style>
